import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MixerPanel from './MixerPanel';

// ── Constants ─────────────────────────────────────────────────────
const GOLD   = '#d4af37';
const NAVY   = '#0a0f1e';
const PURPLE = '#6a0dad';
const GREEN  = '#00ff9d';

const REVERB_PRESETS = [
  { label: 'None',       decay: 0,    wet: 0    },
  { label: 'Room',       decay: 1.5,  wet: 0.15 },
  { label: 'Hall',       decay: 3.0,  wet: 0.25 },
  { label: 'Cathedral',  decay: 5.0,  wet: 0.40 },
  { label: 'Chamber',    decay: 2.0,  wet: 0.20 },
  { label: 'Epic',       decay: 7.0,  wet: 0.55 },
];

// ── Audio Engine ──────────────────────────────────────────────────
class AudioEngine {
  constructor() {
    this.ctx = null;
    this.tracks = {};
    this.masterGain = null;
    this.analyser = null;
  }

  init() {
    if (this.ctx) return;
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    // ── Mix Buses ────────────────────────────────────────────────────
    this.vocalBus = this.ctx.createGain();
    this.musicBus = this.ctx.createGain();
    this.sfxBus   = this.ctx.createGain();
    this.masterGain = this.ctx.createGain();
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 2048;
    // Bus routing: tracks → bus → master → analyser → destination
    this.vocalBus.connect(this.masterGain);
    this.musicBus.connect(this.masterGain);
    this.sfxBus.connect(this.masterGain);
    this.masterGain.connect(this.analyser);
    this.analyser.connect(this.ctx.destination);
  }

  getBus(id) {
    if (['vocalLead','vocalSecondary','vocalBackground'].includes(id)) return this.vocalBus;
    if (['score','theme'].includes(id)) return this.musicBus;
    return this.sfxBus;
  }

  setBusVolume(bus, val) {
    const node = bus === 'vocal' ? this.vocalBus : bus === 'music' ? this.musicBus : this.sfxBus;
    if (node) node.gain.setTargetAtTime(val, this.ctx.currentTime, 0.01);
  }

  async loadTrack(id, url) {
    this.init();
    if (this.tracks[id]) { this.tracks[id].source?.stop(); }
    const res = await fetch(url);
    const buf = await res.arrayBuffer();
    const decoded = await this.ctx.decodeAudioData(buf);
    const gainNode = this.ctx.createGain();
    const panNode = this.ctx.createStereoPanner();
    const reverbGain = this.ctx.createGain();
    const dryGain = this.ctx.createGain();
    gainNode.connect(panNode);
    panNode.connect(dryGain);
    dryGain.connect(this.masterGain);
    panNode.connect(reverbGain);
    reverbGain.connect(this.masterGain);
    reverbGain.gain.value = 0;
    dryGain.gain.value = 1;
    this.tracks[id] = { buffer: decoded, gainNode, panNode, reverbGain, dryGain, source: null, offset: 0, startedAt: 0 };
    return decoded.duration;
  }

  async createReverb(decay, wet) {
    this.init();
    const sampleRate = this.ctx.sampleRate;
    const length = sampleRate * decay;
    const impulse = this.ctx.createBuffer(2, length, sampleRate);
    for (let i = 0; i < 2; i++) {
      const ch = impulse.getChannelData(i);
      for (let j = 0; j < length; j++) {
        ch[j] = (Math.random() * 2 - 1) * Math.pow(1 - j / length, decay);
      }
    }
    const convolver = this.ctx.createConvolver();
    convolver.buffer = impulse;
    return convolver;
  }

  async setReverb(id, decay, wet) {
    const t = this.tracks[id];
    if (!t) return;
    if (t.convolver) { t.panNode.disconnect(t.convolver); t.convolver.disconnect(); }
    if (wet > 0 && decay > 0) {
      const conv = await this.createReverb(decay, wet);
      t.panNode.connect(conv);
      conv.connect(t.reverbGain);
      t.convolver = conv;
    }
    t.reverbGain.gain.setTargetAtTime(wet, this.ctx.currentTime, 0.01);
    t.dryGain.gain.setTargetAtTime(1 - wet * 0.5, this.ctx.currentTime, 0.01);
  }

  setCompression(id, params) {
    const t = this.tracks[id];
    if (!t || !t.compressor) return;
    const c = t.compressor;
    const now = this.ctx.currentTime;
    if (params.enabled === false) {
      c.threshold.setTargetAtTime(0,   now, 0.01);
      c.ratio.setTargetAtTime(1,       now, 0.01);
      c.knee.setTargetAtTime(40,       now, 0.01);
    } else {
      c.threshold.setTargetAtTime(params.threshold ?? -24, now, 0.01);
      c.ratio.setTargetAtTime(params.ratio ?? 4,           now, 0.01);
      c.knee.setTargetAtTime(params.knee ?? 30,            now, 0.01);
      c.attack.setTargetAtTime(params.attack ?? 0.003,     now, 0.01);
      c.release.setTargetAtTime(params.release ?? 0.25,    now, 0.01);
    }
  }

  setEQ(id, band, gainDb) {
    const t = this.tracks[id];
    if (!t) return;
    const now = this.ctx.currentTime;
    if (band === 'sub'     && t.eqSub)     t.eqSub.gain.setTargetAtTime(gainDb,     now, 0.01);
    if (band === 'bass'    && t.eqBass)    t.eqBass.gain.setTargetAtTime(gainDb,    now, 0.01);
    if (band === 'lowMid'  && t.eqLowMid)  t.eqLowMid.gain.setTargetAtTime(gainDb,  now, 0.01);
    if (band === 'mid'     && t.eqMid)     t.eqMid.gain.setTargetAtTime(gainDb,     now, 0.01);
    if (band === 'highMid' && t.eqHighMid) t.eqHighMid.gain.setTargetAtTime(gainDb, now, 0.01);
    if (band === 'high'    && t.eqHigh)    t.eqHigh.gain.setTargetAtTime(gainDb,    now, 0.01);
  }

  setVolume(id, val) {
    const t = this.tracks[id];
    if (t) t.gainNode.gain.setTargetAtTime(val, this.ctx.currentTime, 0.01);
  }

  setPan(id, val) {
    const t = this.tracks[id];
    if (t) t.panNode.pan.setTargetAtTime(val, this.ctx.currentTime, 0.01);
  }

  setMasterVolume(val) {
    if (this.masterGain) this.masterGain.gain.setTargetAtTime(val, this.ctx.currentTime, 0.01);
  }

  playTimeline(id, playheadPos, startOffset) {
    const t = this.tracks[id];
    if (!t || !t.buffer) return;
    if (t.source) { try { t.source.stop(); } catch {} }
    t.source = null;
    const clipEnd = startOffset + t.buffer.duration;
    if (playheadPos >= clipEnd) return; // playhead past clip end
    const src = this.ctx.createBufferSource();
    src.buffer = t.buffer;
    src.connect(t.gainNode);
    if (playheadPos < startOffset) {
      // Playhead is before clip — schedule clip to start in future
      const delay = startOffset - playheadPos;
      src.start(this.ctx.currentTime + delay, 0);
      t.startedAt = this.ctx.currentTime + delay;
      t.offset = 0;
    } else {
      // Playhead is inside clip — start from mid-clip
      const clipOffset = playheadPos - startOffset;
      src.start(0, clipOffset);
      t.startedAt = this.ctx.currentTime - clipOffset;
      t.offset = clipOffset;
    }
    t.source = src;
    return src;
  }

  play(id, offset = 0) {
    const t = this.tracks[id];
    if (!t || !t.buffer) return;
    if (t.source) { try { t.source.stop(); } catch {} }
    const src = this.ctx.createBufferSource();
    src.buffer = t.buffer;
    src.connect(t.gainNode);
    src.start(0, offset);
    t.source = src;
    t.startedAt = this.ctx.currentTime - offset;
    t.offset = offset;
    return src;
  }

  pause(id) {
    const t = this.tracks[id];
    if (!t || !t.source) return 0;
    const elapsed = this.ctx.currentTime - t.startedAt;
    try { t.source.stop(); } catch {}
    t.source = null;
    t.offset = elapsed;
    return elapsed;
  }

  stop(id) {
    const t = this.tracks[id];
    if (!t) return;
    try { t.source?.stop(); } catch {}
    if (t) { t.source = null; t.offset = 0; t.startedAt = 0; }
  }

  getCurrentTime(id) {
    const t = this.tracks[id];
    if (!t) return 0;
    if (t.source) return this.ctx.currentTime - t.startedAt;
    return t.offset;
  }

  getAnalyserData() {
    if (!this.analyser) return new Uint8Array(128);
    const data = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(data);
    return data;
  }

  getWaveformData(id) {
    const t = this.tracks[id];
    if (!t || !t.buffer) return [];
    const data = t.buffer.getChannelData(0);
    const samples = 200;
    const blockSize = Math.floor(data.length / samples);
    const result = [];
    for (let i = 0; i < samples; i++) {
      let sum = 0;
      for (let j = 0; j < blockSize; j++) sum += Math.abs(data[i * blockSize + j]);
      result.push(sum / blockSize);
    }
    const max = Math.max(...result);
    return result.map(v => v / max);
  }
}

const engine = new AudioEngine();

// ── Waveform Display ──────────────────────────────────────────────
function Waveform({ data, progress, color, onClick }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data.length) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    const progressX = progress * W;

    data.forEach((v, i) => {
      const x = (i / data.length) * W;
      const h = v * H * 0.85;
      const y = (H - h) / 2;
      ctx.fillStyle = x < progressX
        ? color
        : `${color}44`;
      ctx.fillRect(x, y, Math.max(1, W / data.length - 1), h);
    });

    // playhead
    if (progress > 0) {
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(progressX, 0);
      ctx.lineTo(progressX, H);
      ctx.stroke();
    }
  }, [data, progress, color]);

  const handleClick = (e) => {
    if (!onClick) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    onClick(x / rect.width);
  };

  return (
    <canvas
      ref={canvasRef}
      width={600}
      height={80}
      onClick={handleClick}
      style={{ width: '100%', height: '80px', cursor: onClick ? 'pointer' : 'default', borderRadius: '6px' }}
    />
  );
}

// ── Spectrum Analyzer ─────────────────────────────────────────────
function SpectrumAnalyzer({ engineRef, isPlaying }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;

    const draw = () => {
      rafRef.current = requestAnimationFrame(draw);
      const data = engine.getAnalyserData();
      ctx.fillStyle = 'rgba(10,15,30,0.3)';
      ctx.fillRect(0, 0, W, H);
      const barW = W / data.length * 2.5;
      for (let i = 0; i < data.length; i++) {
        const v = data[i] / 255;
        const h = v * H;
        const hue = 200 + v * 60;
        ctx.fillStyle = `hsla(${hue}, 80%, 60%, 0.9)`;
        ctx.fillRect(i * barW, H - h, barW - 1, h);
      }
    };
    draw();
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPlaying]);

  return (
    <canvas
      ref={canvasRef}
      width={600}
      height={60}
      style={{ width: '100%', height: '60px', borderRadius: '6px', background: 'rgba(10,15,30,0.5)' }}
    />
  );
}

// ── VU Meter ──────────────────────────────────────────────────────
function VUMeter({ level }) {
  const bars = 20;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', height: '120px', justifyContent: 'flex-end' }}>
      {Array.from({ length: bars }).map((_, i) => {
        const threshold = (bars - i) / bars;
        const active = level > threshold;
        const isRed = i < 2;
        const isYellow = i < 5;
        return (
          <div key={i} style={{
            height: '4px', borderRadius: '2px',
            background: active
              ? isRed ? '#ff4444' : isYellow ? '#ffaa00' : GREEN
              : 'rgba(255,255,255,0.1)',
            transition: 'background 0.05s',
          }} />
        );
      })}
    </div>
  );
}

// ── Knob ──────────────────────────────────────────────────────────
function Knob({ value, min, max, onChange, label, color = GOLD, size = 56 }) {
  const ref = useRef(null);
  const startY = useRef(0);
  const startVal = useRef(0);

  const pct = (value - min) / (max - min);
  const angle = -135 + pct * 270;

  const onMouseDown = (e) => {
    startY.current = e.clientY;
    startVal.current = value;
    const onMove = (me) => {
      const delta = (startY.current - me.clientY) / 150;
      const newVal = Math.min(max, Math.max(min, startVal.current + delta * (max - min)));
      onChange(newVal);
    };
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', userSelect: 'none' }}>
      <div
        ref={ref}
        onMouseDown={onMouseDown}
        style={{
          width: size, height: size, borderRadius: '50%',
          background: `conic-gradient(${color} ${pct * 100}%, rgba(255,255,255,0.08) ${pct * 100}%)`,
          border: `2px solid ${color}44`,
          cursor: 'ns-resize', position: 'relative',
          boxShadow: `0 0 12px ${color}33`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div style={{
          width: '3px', height: size * 0.35,
          background: color, borderRadius: '2px',
          transform: `rotate(${angle}deg)`,
          transformOrigin: 'bottom center',
          position: 'absolute', bottom: '50%', left: '50%',
          marginLeft: '-1.5px',
        }} />
        <div style={{
          width: size * 0.45, height: size * 0.45,
          borderRadius: '50%', background: '#0a0f1e',
          border: `1px solid rgba(255,255,255,0.1)`,
        }} />
      </div>
      <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
    </div>
  );
}

// ── Fader ─────────────────────────────────────────────────────────
function Fader({ value, onChange, color = GOLD }) {
  const trackRef = useRef(null);

  const handleClick = (e) => {
    const rect = trackRef.current.getBoundingClientRect();
    const pct = 1 - (e.clientY - rect.top) / rect.height;
    onChange(Math.min(1, Math.max(0, pct)));
  };

  return (
    <div
      ref={trackRef}
      onClick={handleClick}
      style={{
        width: '24px', height: '140px',
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '12px', position: 'relative', cursor: 'pointer',
      }}
    >
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        height: `${value * 100}%`,
        background: `linear-gradient(to top, ${color}, ${color}88)`,
        borderRadius: '12px', transition: 'height 0.05s',
      }} />
      <div style={{
        position: 'absolute',
        bottom: `calc(${value * 100}% - 10px)`,
        left: '-4px', right: '-4px',
        height: '20px', background: '#fff',
        borderRadius: '4px', boxShadow: `0 0 8px ${color}`,
        transition: 'bottom 0.05s',
      }} />
    </div>
  );
}

// ── Compressor Strip ──────────────────────────────────────────────
function CompressorStrip({ comp, onChange, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '220px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Compressor</div>
        <button
          onClick={() => onChange({ enabled: !comp.enabled })}
          style={{
            fontSize: '0.7rem', padding: '3px 10px', borderRadius: '10px', cursor: 'pointer', border: 'none',
            background: comp.enabled ? color : 'rgba(255,255,255,0.08)',
            color: comp.enabled ? '#0a0f1e' : 'rgba(255,255,255,0.4)',
            fontWeight: '700', letterSpacing: '0.04em', transition: 'all 0.2s',
          }}
        >{comp.enabled ? 'ON' : 'OFF'}</button>
      </div>
      <div style={{ display: 'flex', gap: '16px', opacity: comp.enabled ? 1 : 0.35, transition: 'opacity 0.2s' }}>
        {/* Threshold */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.3)' }}>-60 → 0</span>
          <Knob
            value={comp.threshold}
            min={-60} max={0}
            onChange={(v) => onChange({ threshold: Math.round(v) })}
            label="Thresh"
            color={color}
            size={48}
          />
          <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)' }}>{comp.threshold} dB</span>
        </div>
        {/* Ratio */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.3)' }}>1 → 20</span>
          <Knob
            value={comp.ratio}
            min={1} max={20}
            onChange={(v) => onChange({ ratio: Math.round(v * 2) / 2 })}
            label="Ratio"
            color={color}
            size={48}
          />
          <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)' }}>{comp.ratio}:1</span>
        </div>
        {/* Knee */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.3)' }}>0 → 40</span>
          <Knob
            value={comp.knee}
            min={0} max={40}
            onChange={(v) => onChange({ knee: Math.round(v) })}
            label="Knee"
            color={color}
            size={48}
          />
          <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)' }}>{comp.knee} dB</span>
        </div>
      </div>
    </div>
  );
}

// ── EQ Strip ──────────────────────────────────────────────────────
function EQStrip({ eq, onChange, color }) {
  const bands = [
    { key: 'sub',     label: 'Sub',     hint: '40 Hz',   boost: '#ff6600' },
    { key: 'bass',    label: 'Bass',    hint: '100 Hz',  boost: '#ff8833' },
    { key: 'lowMid',  label: 'Low Mid', hint: '300 Hz',  boost: '#ffaa44' },
    { key: 'mid',     label: 'Mid',     hint: '1 kHz',   boost: '#ffcc55' },
    { key: 'highMid', label: 'Hi Mid',  hint: '5 kHz',   boost: '#44aaff' },
    { key: 'high',    label: 'High',    hint: '12 kHz',  boost: '#66ccff' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '300px' }}>
      <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>6-Band EQ</div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'nowrap' }}>
        {bands.map(({ key, label, hint, boost }) => {
          const val = eq[key] ?? 0;
          const knobColor = val > 0.5 ? boost : val < -0.5 ? '#44aaff' : color;
          return (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
              <span style={{ fontSize: '8px', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.03em', whiteSpace: 'nowrap' }}>{hint}</span>
              <Knob
                value={val}
                min={-12}
                max={12}
                onChange={(v) => onChange(key, Math.round(v * 2) / 2)}
                label={label}
                color={knobColor}
                size={44}
              />
              <span style={{ fontSize: '8px', color: val > 0.5 ? boost : val < -0.5 ? '#44aaff' : 'rgba(255,255,255,0.3)', whiteSpace: 'nowrap' }}>
                {val > 0 ? '+' : ''}{val.toFixed(1)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Track Strip ───────────────────────────────────────────────────
function TrackStrip({ id, label, color, track, onVolumeChange, onPanChange, onReverbChange, onEQChange, onCompChange, onSeek, isPlaying, onMute, onSolo }) {
  const [waveform, setWaveform] = useState([]);

  useEffect(() => {
    if (track.loaded) setWaveform(engine.getWaveformData(id));
  }, [track.loaded, id]);

  const progress = track.duration > 0 ? track.currentTime / track.duration : 0;

  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: `1px solid ${color}33`,
      borderRadius: '16px', padding: '20px',
      display: 'flex', flexDirection: 'column', gap: '14px',
    }}>
      {/* Track header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: track.loaded ? color : 'rgba(255,255,255,0.2)', boxShadow: track.loaded ? `0 0 8px ${color}` : 'none' }} />
          <span style={{ fontSize: '0.85rem', fontWeight: '700', color, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
          {track.duration > 0 && (
            <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>
              {Math.floor(track.currentTime / 60)}:{String(Math.floor(track.currentTime % 60)).padStart(2, '0')} / {Math.floor(track.duration / 60)}:{String(Math.floor(track.duration % 60)).padStart(2, '0')}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={onMute} style={{ ...btn, background: track.muted ? '#ff4444' : 'rgba(255,255,255,0.06)', color: track.muted ? '#fff' : 'rgba(255,255,255,0.4)', fontSize: '0.75rem', padding: '4px 10px' }}>M</button>
          <button onClick={onSolo} style={{ ...btn, background: track.solo ? GOLD : 'rgba(255,255,255,0.06)', color: track.solo ? NAVY : 'rgba(255,255,255,0.4)', fontSize: '0.75rem', padding: '4px 10px' }}>S</button>
        </div>
      </div>

      {/* Waveform */}
      <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '4px', minHeight: '88px', display: 'flex', alignItems: 'center' }}>
        {track.loaded
          ? <Waveform data={waveform} progress={progress} color={color} onClick={onSeek} />
          : <div style={{ width: '100%', textAlign: 'center', color: 'rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>
              {track.loading ? '⟳ Loading...' : 'No audio loaded'}
            </div>
        }
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '24px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px' }}>
          <Fader value={track.volume} onChange={onVolumeChange} color={color} />
          <VUMeter level={track.muted ? 0 : track.volume * (isPlaying ? 0.5 + Math.random() * 0.5 : 0)} />
        </div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <Knob value={track.pan} min={-1} max={1} onChange={onPanChange} label="Pan" color={color} />
          <Knob value={track.volume} min={0} max={1} onChange={onVolumeChange} label="Vol" color={color} />
        </div>
        <div style={{ flex: 1, minWidth: '160px' }}>
          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', marginBottom: '8px', textTransform: 'uppercase' }}>Reverb</div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {REVERB_PRESETS.map(p => (
              <button key={p.label} onClick={() => onReverbChange(p.decay, p.wet)}
                style={{ ...btn, fontSize: '0.72rem', padding: '4px 10px', background: track.reverb === p.label ? `${color}22` : 'rgba(255,255,255,0.05)', border: `1px solid ${track.reverb === p.label ? color : 'rgba(255,255,255,0.1)'}`, color: track.reverb === p.label ? color : 'rgba(255,255,255,0.5)' }}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <EQStrip eq={track.eq} onChange={onEQChange} color={color} />
        <CompressorStrip comp={track.comp} onChange={onCompChange} color={color} />
      </div>

    </div>
  );
}

const btn = {
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '6px', color: 'rgba(255,255,255,0.7)', padding: '8px 14px',
  cursor: 'pointer', fontSize: '0.85rem', transition: 'all 0.15s',
};

// ── Timeline View ─────────────────────────────────────────────────
const VIDEO_LANE_ORDER = [
  { id: 'videoPrimary', label: 'Video — Primary', color: '#ef4444' },
  { id: 'videoBroll',   label: 'Video — B-Roll',  color: '#f97316' },
];

const TRACK_ORDER = [
  { id: 'vocalLead',       label: 'Vocal Lead',        color: '#6a0dad' },
  { id: 'vocalSecondary',  label: 'Vocal Secondary',   color: '#9b30ff' },
  { id: 'vocalBackground', label: 'Vocal Background',  color: '#c084fc' },
  { id: 'score',           label: 'Score',             color: '#d4af37' },
  { id: 'theme',           label: 'Theme',             color: '#f59e0b' },
  { id: 'sfxFoley',        label: 'SFX Foley',         color: '#00ff9d' },
  { id: 'sfxAmbient',      label: 'SFX Ambient',       color: '#34d399' },
  { id: 'sfxCinematic',    label: 'SFX Cinematic',     color: '#6ee7b7' },
];

const LANE_H    = 52;
const LABEL_W   = 110;
const RULER_H   = 24;

function TimelineView({ tracks, playheadPos, zoom, onSeek, onOffsetChange, onZoomChange }) {
  const canvasRef  = useRef(null);
  const dragRef    = useRef(null); // { id, startX, startOffset }
  const containerRef = useRef(null);

  // Total timeline duration
  const totalDuration = Math.max(30, ...Object.values(tracks).map(t => t.startOffset + t.duration));
  const totalW = totalDuration * zoom;

  // Draw timeline
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    // Background
    ctx.fillStyle = '#070b14';
    ctx.fillRect(0, 0, W, H);

    // Time ruler
    ctx.fillStyle = '#0d1530';
    ctx.fillRect(0, 0, W, RULER_H);
    ctx.strokeStyle = 'rgba(212,175,55,0.2)';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, RULER_H); ctx.lineTo(W, RULER_H); ctx.stroke();

    // Ruler ticks
    const step = zoom >= 80 ? 1 : zoom >= 40 ? 2 : zoom >= 20 ? 5 : 10;
    for (let s = 0; s <= totalDuration; s += step) {
      const x = s * zoom;
      ctx.strokeStyle = 'rgba(212,175,55,0.25)';
      ctx.lineWidth = s % (step * 5) === 0 ? 1.5 : 0.5;
      ctx.beginPath(); ctx.moveTo(x, RULER_H - 8); ctx.lineTo(x, RULER_H); ctx.stroke();
      if (s % (step * 5) === 0 || step === 1) {
        const mins = Math.floor(s / 60);
        const secs = s % 60;
        const label = mins > 0 ? `${mins}:${String(secs).padStart(2,'0')}` : `${secs}s`;
        ctx.fillStyle = 'rgba(212,175,55,0.6)';
        ctx.font = '10px monospace';
        ctx.fillText(label, x + 3, RULER_H - 10);
      }
    }

    // Track lanes
    TRACK_ORDER.forEach(({ id, label, color }, i) => {
      const t = tracks[id];
      const y = RULER_H + i * LANE_H;

      // Lane background
      ctx.fillStyle = i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.01)';
      ctx.fillRect(0, y, W, LANE_H);

      // Lane separator
      ctx.strokeStyle = 'rgba(255,255,255,0.04)';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(0, y + LANE_H); ctx.lineTo(W, y + LANE_H); ctx.stroke();

      // Beat grid lines
      for (let s = 0; s <= totalDuration; s += step) {
        if (s % (step * 5) === 0) {
          ctx.strokeStyle = 'rgba(255,255,255,0.04)';
          ctx.lineWidth = 0.5;
          ctx.beginPath(); ctx.moveTo(s * zoom, y); ctx.lineTo(s * zoom, y + LANE_H); ctx.stroke();
        }
      }

      if (!t.loaded) return;

      // Clip block
      const clipX = t.startOffset * zoom;
      const clipW = Math.max(4, t.duration * zoom);
      const clipY = y + 4;
      const clipH = LANE_H - 8;

      // Clip fill
      ctx.fillStyle = `${color}28`;
      ctx.beginPath();
      ctx.roundRect(clipX, clipY, clipW, clipH, 4);
      ctx.fill();

      // Clip border
      ctx.strokeStyle = `${color}99`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(clipX, clipY, clipW, clipH, 4);
      ctx.stroke();

      // Waveform inside clip
      const wData = engine.getWaveformData(id);
      if (wData.length > 0) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.beginPath();
        const midY = clipY + clipH / 2;
        wData.forEach((v, wi) => {
          const wx = clipX + (wi / wData.length) * clipW;
          const wh = v * clipH * 0.45;
          if (wi === 0) ctx.moveTo(wx, midY - wh);
          else ctx.lineTo(wx, midY - wh);
        });
        wData.slice().reverse().forEach((v, wi) => {
          const wx = clipX + (1 - wi / wData.length) * clipW;
          const wh = v * clipH * 0.45;
          ctx.lineTo(wx, midY + wh);
        });
        ctx.closePath();
        ctx.fillStyle = `${color}33`;
        ctx.fill();
        ctx.strokeStyle = `${color}88`;
        ctx.stroke();
      }

      // Muted overlay
      if (t.muted) {
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.beginPath(); ctx.roundRect(clipX, clipY, clipW, clipH, 4); ctx.fill();
        ctx.fillStyle = 'rgba(255,80,80,0.6)';
        ctx.font = 'bold 11px sans-serif';
        ctx.fillText('MUTED', clipX + 6, clipY + clipH / 2 + 4);
      }

      // Offset time badge
      if (t.startOffset > 0) {
        ctx.fillStyle = `${color}cc`;
        ctx.font = '9px monospace';
        ctx.fillText(`+${t.startOffset.toFixed(1)}s`, clipX + 4, clipY + 10);
      }
    });

    // Playhead
    const phX = playheadPos * zoom;
    ctx.strokeStyle = '#ff4444';
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(phX, 0); ctx.lineTo(phX, H); ctx.stroke();
    // Playhead triangle
    ctx.fillStyle = '#ff4444';
    ctx.beginPath(); ctx.moveTo(phX - 6, 0); ctx.lineTo(phX + 6, 0); ctx.lineTo(phX, 10); ctx.fill();
    // Playhead time
    const phMins = Math.floor(playheadPos / 60);
    const phSecs = (playheadPos % 60).toFixed(1);
    const phLabel = phMins > 0 ? `${phMins}:${String(Math.floor(phSecs)).padStart(2,'0')}.${String(phSecs).split('.')[1]}` : `${phSecs}s`;
    ctx.fillStyle = '#ff4444';
    ctx.font = 'bold 10px monospace';
    ctx.fillText(phLabel, Math.min(phX + 4, W - 50), 14);

  }, [tracks, playheadPos, zoom, totalDuration]);

  // Canvas size
  const canvasW = Math.max(totalW, containerRef.current?.clientWidth || 800);
  const canvasH = RULER_H + TRACK_ORDER.length * LANE_H;

  // Click ruler → seek
  const handleCanvasClick = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + (containerRef.current?.scrollLeft || 0);
    const y = e.clientY - rect.top;
    if (y < RULER_H) {
      onSeek(x / zoom);
      return;
    }
    // Click on clip body starts drag preparation
  };

  // Drag to reposition clips
  const handleMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + (containerRef.current?.scrollLeft || 0);
    const y = e.clientY - rect.top;
    if (y < RULER_H) { onSeek(x / zoom); return; }

    const laneIndex = Math.floor((y - RULER_H) / LANE_H);
    if (laneIndex < 0 || laneIndex >= TRACK_ORDER.length) return;
    const { id } = TRACK_ORDER[laneIndex];
    const t = tracks[id];
    if (!t.loaded) return;

    const clipX = t.startOffset * zoom;
    const clipW = t.duration * zoom;
    if (x >= clipX && x <= clipX + clipW) {
      dragRef.current = { id, startMouseX: e.clientX, startOffset: t.startOffset };
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (!dragRef.current) return;
    const { id, startMouseX, startOffset } = dragRef.current;
    const dx = e.clientX - startMouseX;
    const newOffset = Math.max(0, startOffset + dx / zoom);
    onOffsetChange(id, newOffset);
  };

  const handleMouseUp = () => { dragRef.current = null; };

  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', overflow: 'hidden' }}>
      {/* Timeline header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Timeline &nbsp;·&nbsp; {TRACK_ORDER.length} tracks &nbsp;·&nbsp; {totalDuration.toFixed(1)}s total
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)' }}>Zoom</span>
          <input type="range" min={10} max={200} step={5} value={zoom}
            onChange={e => onZoomChange(parseInt(e.target.value))}
            style={{ width: '100px', accentColor: '#d4af37', cursor: 'pointer' }}
          />
          <span style={{ fontSize: '0.75rem', color: '#d4af37', minWidth: '40px' }}>{zoom}px/s</span>
        </div>
      </div>

      {/* Track labels + canvas */}
      <div style={{ display: 'flex' }}>
        {/* Fixed label column */}
        <div style={{ width: `${LABEL_W}px`, flexShrink: 0, borderRight: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ height: `${RULER_H}px`, background: '#0d1530', borderBottom: '1px solid rgba(212,175,55,0.15)' }} />
          {TRACK_ORDER.map(({ id, label, color }) => (
            <div key={id} style={{
              height: `${LANE_H}px`, display: 'flex', alignItems: 'center', paddingLeft: '10px',
              borderBottom: '1px solid rgba(255,255,255,0.04)',
              background: tracks[id].muted ? 'rgba(255,50,50,0.06)' : 'rgba(255,255,255,0.01)',
            }}>
              <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: tracks[id].loaded ? color : 'rgba(255,255,255,0.15)', marginRight: '7px', flexShrink: 0, boxShadow: tracks[id].loaded ? `0 0 6px ${color}` : 'none' }} />
              <span style={{ fontSize: '0.7rem', color: tracks[id].loaded ? color : 'rgba(255,255,255,0.25)', fontWeight: '600', letterSpacing: '0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
            </div>
          ))}
        </div>

        {/* Scrollable canvas area */}
        <div ref={containerRef} style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', cursor: 'default' }}>
          <canvas
            ref={canvasRef}
            width={canvasW}
            height={canvasH}
            onClick={handleCanvasClick}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{ display: 'block', cursor: 'crosshair' }}
          />
        </div>
      </div>

      <div style={{ padding: '8px 16px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.2)', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        Click ruler to seek · Drag clip to reposition · Zoom to adjust timeline scale
      </div>
    </div>
  );
}

// ── MIDI Panel ────────────────────────────────────────────────────
function MidiPanel({ midiEnabled, midiDevices, midiDevice, midiLearn, midiMap,
                     onInit, onConnect, onLearn, onClearMap }) {

  const LEARNABLE = [
    { label: 'Master Volume',    param: 'masterVolume', id: null },
    { label: 'Vocal Bus',        param: 'busVocal',     id: null },
    { label: 'Music Bus',        param: 'busMusic',     id: null },
    { label: 'SFX Bus',          param: 'busSfx',       id: null },
    { label: 'Vocal Lead Vol',   param: 'volume',       id: 'vocalLead' },
    { label: 'Vocal Sec Vol',    param: 'volume',       id: 'vocalSecondary' },
    { label: 'Score Volume',     param: 'volume',       id: 'score' },
    { label: 'Theme Volume',     param: 'volume',       id: 'theme' },
    { label: 'Score Sub EQ',     param: 'eqSub',        id: 'score' },
    { label: 'Score Bass EQ',    param: 'eqBass',       id: 'score' },
    { label: 'Theme Sub EQ',     param: 'eqSub',        id: 'theme' },
    { label: 'Theme Bass EQ',    param: 'eqBass',       id: 'theme' },
    { label: 'Play',             param: 'play',         id: null, note: true },
    { label: 'Pause',            param: 'pause',        id: null, note: true },
    { label: 'Stop',             param: 'stop',         id: null, note: true },
  ];

  const getMappedCC = (param, id) => {
    const entry = Object.entries(midiMap).find(([, v]) => v.param === param && v.id === id);
    return entry ? entry[0].replace('cc_', 'CC').replace('note_', 'Note ') : null;
  };

  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', padding: '20px' }}>
      <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '14px' }}>
        🎹 MIDI Controller
      </div>

      {!midiEnabled ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
          <button onClick={onInit} style={{ ...btn, background: `${GOLD}22`, border: `1px solid ${GOLD}`, color: GOLD, fontWeight: '700', padding: '10px 20px' }}>
            🎹 Connect MIDI Device
          </button>
          <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)' }}>
            Connect your Yamaha keyboard via USB, then click to enable MIDI. Requires Chrome or Edge.
          </span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

          {/* Device selector */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', minWidth: '100px' }}>MIDI Input</span>
            <select
              value={midiDevice || ''}
              onChange={e => onConnect(e.target.value)}
              style={{ flex: 1, background: '#1a1a2e', color: GOLD, border: `1px solid ${GOLD}44`, borderRadius: '8px', padding: '8px 12px', fontSize: '0.85rem', cursor: 'pointer', outline: 'none' }}
            >
              <option value=''>-- Select Device --</option>
              {midiDevices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: midiDevice ? '#00ff9d' : 'rgba(255,255,255,0.2)', boxShadow: midiDevice ? '0 0 8px #00ff9d' : 'none' }} />
          </div>

          {/* MIDI Learn mappings */}
          {midiDevice && (
            <>
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                MIDI Learn — click a parameter then move a knob/fader on your keyboard
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {LEARNABLE.map(({ label, param, id }) => {
                  const mapped = getMappedCC(param, id);
                  const isLearning = midiLearn && midiLearn.param === param && midiLearn.id === id;
                  return (
                    <button
                      key={`${param}-${id}`}
                      onClick={() => onLearn(isLearning ? null : { param, id })}
                      style={{
                        ...btn,
                        fontSize: '0.72rem', padding: '5px 12px',
                        background: isLearning ? '#ff4444' : mapped ? `${GOLD}18` : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${isLearning ? '#ff4444' : mapped ? GOLD : 'rgba(255,255,255,0.1)'}`,
                        color: isLearning ? '#fff' : mapped ? GOLD : 'rgba(255,255,255,0.5)',
                        animation: isLearning ? 'pulse 1s infinite' : 'none',
                      }}
                    >
                      {label}{mapped ? ` [${mapped}]` : ''}{isLearning ? ' — Move Control' : ''}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)' }}>
                  {Object.keys(midiMap).length} mappings saved
                </span>
                {Object.keys(midiMap).length > 0 && (
                  <button onClick={onClearMap} style={{ ...btn, fontSize: '0.72rem', padding: '4px 12px', color: '#ff6060', border: '1px solid rgba(255,60,60,0.3)' }}>
                    Clear All Mappings
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Bus Strip ─────────────────────────────────────────────────────
function BusStrip({ busId, label, color, bus, onChange }) {
  return (
    <div style={{
      background: `${color}0a`, border: `1px solid ${color}44`,
      borderRadius: '14px', padding: '16px 20px',
      display: 'flex', alignItems: 'center', gap: '20px', flex: 1, minWidth: '200px',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '80px' }}>
        <span style={{ fontSize: '0.75rem', fontWeight: '700', color, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</span>
        <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)' }}>Bus</span>
      </div>
      <Fader value={bus.volume} onChange={(v) => onChange({ volume: v })} color={color} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
        <Knob value={bus.volume} min={0} max={1} onChange={(v) => onChange({ volume: v })} label="Level" color={color} size={44} />
        <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)' }}>{Math.round(bus.volume * 100)}%</span>
      </div>
      <button
        onClick={() => onChange({ muted: !bus.muted })}
        style={{
          padding: '6px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer',
          background: bus.muted ? '#ff4444' : `${color}22`,
          color: bus.muted ? '#fff' : color,
          fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.04em',
          transition: 'all 0.2s',
        }}
      >{bus.muted ? 'MUTED' : 'M'}</button>
      <VUMeter level={bus.muted ? 0 : bus.volume * 0.85} />
    </div>
  );
}

// ── Main Soundboard ───────────────────────────────────────────────
export default function StudioSoundboard() {
  const [activeTab, setActiveTab] = React.useState('soundboard'); // 'soundboard' | 'mixer'
  const navigate = useNavigate();

  const [tracks, setTracks] = useState({
    vocalLead:       { loaded: false, loading: false, volume: 0.90, pan:  0.0,  reverb: 'None',    eq: { sub: 0, bass: 0, lowMid: 0, mid: 0, highMid: 0, high: 0 }, comp: { enabled: true,  threshold: -18, ratio: 4,  knee: 10, attack: 0.003, release: 0.25 }, muted: false, solo: false, duration: 0, currentTime: 0, fileName: '', startOffset: 0 },
    vocalSecondary:  { loaded: false, loading: false, volume: 0.80, pan:  0.1,  reverb: 'None',    eq: { sub: 0, bass: 0, lowMid: 0, mid: 0, highMid: 0, high: 0 }, comp: { enabled: true,  threshold: -20, ratio: 4,  knee: 10, attack: 0.003, release: 0.25 }, muted: false, solo: false, duration: 0, currentTime: 0, fileName: '', startOffset: 0 },
    vocalBackground: { loaded: false, loading: false, volume: 0.45, pan:  0.0,  reverb: 'Room',    eq: { sub: 0, bass:-2, lowMid:-3, mid: 0, highMid: 0, high: 0 }, comp: { enabled: true,  threshold: -24, ratio: 6,  knee: 15, attack: 0.003, release: 0.30 }, muted: false, solo: false, duration: 0, currentTime: 0, fileName: '', startOffset: 0 },
    score:           { loaded: false, loading: false, volume: 0.65, pan:  0.0,  reverb: 'Hall',    eq: { sub: 5, bass: 3, lowMid:-3, mid:-2, highMid: 0, high: 0 }, comp: { enabled: true,  threshold: -20, ratio: 3,  knee: 20, attack: 0.010, release: 0.40 }, muted: false, solo: false, duration: 0, currentTime: 0, fileName: '', startOffset: 0 },
    theme:           { loaded: false, loading: false, volume: 0.75, pan:  0.0,  reverb: 'None',    eq: { sub: 4, bass: 3, lowMid:-2, mid: 0, highMid: 0, high: 1 }, comp: { enabled: false, threshold: -24, ratio: 4,  knee: 30, attack: 0.003, release: 0.25 }, muted: false, solo: false, duration: 0, currentTime: 0, fileName: '', startOffset: 0 },
    sfxFoley:        { loaded: false, loading: false, volume: 0.55, pan:  0.0,  reverb: 'Room',    eq: { sub: 0, bass:-2, lowMid: 0, mid: 0, highMid: 1, high: 2 }, comp: { enabled: false, threshold: -24, ratio: 4,  knee: 30, attack: 0.003, release: 0.25 }, muted: false, solo: false, duration: 0, currentTime: 0, fileName: '', startOffset: 0 },
    sfxAmbient:      { loaded: false, loading: false, volume: 0.40, pan:  0.0,  reverb: 'Hall',    eq: { sub: 3, bass: 2, lowMid:-3, mid:-4, highMid:-1, high:-2 }, comp: { enabled: true,  threshold: -28, ratio: 8,  knee: 20, attack: 0.020, release: 0.50 }, muted: false, solo: false, duration: 0, currentTime: 0, fileName: '', startOffset: 0 },
    sfxCinematic:    { loaded: false, loading: false, volume: 0.70, pan:  0.0,  reverb: 'Chamber', eq: { sub: 6, bass: 4, lowMid:-2, mid: 0, highMid: 1, high: 3 }, comp: { enabled: false, threshold: -24, ratio: 4,  knee: 30, attack: 0.003, release: 0.25 }, muted: false, solo: false, duration: 0, currentTime: 0, fileName: '', startOffset: 0 },
    videoPrimary:    { type: 'video', loaded: false, loading: false, muted: false, solo: false, duration: 0, fileName: '', url: null, thumbnail: null, startOffset: 0, videoMuted: false, videoVolume: 1.0 },
    videoBroll:      { type: 'video', loaded: false, loading: false, muted: false, solo: false, duration: 0, fileName: '', url: null, thumbnail: null, startOffset: 0, videoMuted: false, videoVolume: 1.0 },
  });
  const [masterVolume, setMasterVolume] = useState(0.85);
  const [playheadPos, setPlayheadPos] = useState(0);
  const [timelineZoom, setTimelineZoom] = useState(50); // px per second
  const [showTimeline, setShowTimeline] = useState(false);
  const [buses, setBuses] = useState({
    vocal: { volume: 1.0, muted: false },
    music: { volume: 0.85, muted: false },
    sfx:   { volume: 0.75, muted: false },
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [exportStatus, setExportStatus] = useState('');
  const [mixName, setMixName] = useState('FHO_Chronicles_Mix');
  const [fadeIn, setFadeIn]   = useState(1.0);
  const [fadeOut, setFadeOut] = useState(2.0);
  const [sessionName, setSessionName] = useState('');
  const [sessions, setSessions] = useState(() => {
    try { return JSON.parse(localStorage.getItem('fho_soundboard_sessions') || '{}'); }
    catch { return {}; }
  });
  const [showSessions, setShowSessions] = useState(false);
  const [midiEnabled, setMidiEnabled]   = useState(false);
  const [midiDevices, setMidiDevices]   = useState([]);
  const [midiDevice, setMidiDevice]     = useState(null);
  const [midiLearn, setMidiLearn]       = useState(null); // { param, id } currently learning
  const [midiMap, setMidiMap]           = useState(() => {
    try { return JSON.parse(localStorage.getItem('fho_midi_map') || '{}'); }
    catch { return {}; }
  });
  const [showMidi, setShowMidi]         = useState(false);
  const midiAccessRef = useRef(null);
  const rafRef = useRef(null);
  const fileRefsObj = useRef({
    vocalLead: null, vocalSecondary: null, vocalBackground: null,
    score: null, theme: null,
    sfxFoley: null, sfxAmbient: null, sfxCinematic: null,
    videoPrimary: null, videoBroll: null,
  });
  const [fileKeys, setFileKeys] = useState({
    vocalLead: 0, vocalSecondary: 0, vocalBackground: 0,
    score: 0, theme: 0, sfxFoley: 0, sfxAmbient: 0, sfxCinematic: 0,
    videoPrimary: 0, videoBroll: 0,
  });

  // ── Video preview state (Phase 2 Commit 3) ──────────────────────
  const [activePreview, setActivePreview] = useState('videoPrimary');
  const videoElsRef = useRef({ videoPrimary: null, videoBroll: null });

  // ── Clock tick ────────────────────────────────────────────────────
  const playStartRef = useRef({ ctxTime: 0, playhead: 0 });

  useEffect(() => {
    const tick = () => {
      if (isPlaying && engine.ctx) {
        const elapsed = engine.ctx.currentTime - playStartRef.current.ctxTime;
        const newPh = playStartRef.current.playhead + elapsed;
        setPlayheadPos(newPh);
        setTracks(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(id => {
            if (updated[id].loaded) {
              const clipPos = newPh - updated[id].startOffset;
              updated[id] = { ...updated[id], currentTime: Math.max(0, Math.min(clipPos, updated[id].duration)) };
            }
          });
          return updated;
        });
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPlaying]);

  useEffect(() => { engine.setMasterVolume(masterVolume); }, [masterVolume]);

  // ── Video sync effect (Phase 2 Commit 3) ────────────────────────
  useEffect(() => {
    const activeTrack = tracks[activePreview];
    const activeEl = videoElsRef.current[activePreview];
    const inactiveId = activePreview === 'videoPrimary' ? 'videoBroll' : 'videoPrimary';
    const inactiveEl = videoElsRef.current[inactiveId];

    if (inactiveEl && !inactiveEl.paused) {
      try { inactiveEl.pause(); } catch (e) {}
    }

    if (!activeEl || !activeTrack || !activeTrack.loaded) return;

    activeEl.muted = !!activeTrack.videoMuted;
    activeEl.volume = (activeTrack.videoVolume == null) ? 1.0 : activeTrack.videoVolume;
    const desiredTime = Math.max(0, playheadPos - (activeTrack.startOffset || 0));

    if (isPlaying) {
      if (Math.abs(activeEl.currentTime - desiredTime) > 0.15) {
        try { activeEl.currentTime = Math.min(desiredTime, activeTrack.duration - 0.05); } catch (e) {}
      }
      if (activeEl.paused) {
        activeEl.play().catch(() => {});
      }
    } else {
      if (!activeEl.paused) {
        try { activeEl.pause(); } catch (e) {}
      }
      if (Math.abs(activeEl.currentTime - desiredTime) > 0.05) {
        try { activeEl.currentTime = Math.min(desiredTime, activeTrack.duration - 0.05); } catch (e) {}
      }
    }
  }, [isPlaying, playheadPos, activePreview, tracks]);

  // ── MIDI Engine ───────────────────────────────────────────────────
  const initMidi = async () => {
    if (!navigator.requestMIDIAccess) {
      alert('Web MIDI API not supported in this browser. Use Chrome or Edge for MIDI support.');
      return;
    }
    try {
      const access = await navigator.requestMIDIAccess();
      midiAccessRef.current = access;
      const devices = Array.from(access.inputs.values());
      setMidiDevices(devices.map(d => ({ id: d.id, name: d.name })));
      setMidiEnabled(true);
      access.onstatechange = () => {
        const updated = Array.from(access.inputs.values());
        setMidiDevices(updated.map(d => ({ id: d.id, name: d.name })));
      };
    } catch (e) {
      alert('MIDI access denied. Please allow MIDI access in browser permissions.');
    }
  };

  const connectMidiDevice = (deviceId) => {
    if (!midiAccessRef.current) return;
    // Disconnect all first
    Array.from(midiAccessRef.current.inputs.values()).forEach(d => { d.onmidimessage = null; });
    const device = Array.from(midiAccessRef.current.inputs.values()).find(d => d.id === deviceId);
    if (!device) return;
    setMidiDevice(deviceId);
    device.onmidimessage = (e) => handleMidiMessage(e);
  };

  const handleMidiMessage = (e) => {
    const [status, cc, value] = e.data;
    const isCC = (status & 0xf0) === 0xb0;
    const isNote = (status & 0xf0) === 0x90;
    const isNoteOff = (status & 0xf0) === 0x80;
    const normalized = value / 127; // 0.0 to 1.0

    // MIDI Learn mode — capture incoming CC and map it
    if (midiLearn && isCC) {
      const key = `cc_${cc}`;
      const newMap = { ...midiMap, [key]: midiLearn };
      setMidiMap(newMap);
      localStorage.setItem('fho_midi_map', JSON.stringify(newMap));
      setMidiLearn(null);
      return;
    }

    // Transport note triggers (any note-on)
    if (isNote && value > 0) {
      const noteMap = midiMap[`note_${cc}`];
      if (noteMap === 'play')  { play();  return; }
      if (noteMap === 'pause') { pause(); return; }
      if (noteMap === 'stop')  { stop();  return; }
    }

    // CC routing
    if (!isCC) return;
    const key = `cc_${cc}`;
    const mapping = midiMap[key];
    if (!mapping) return;

    const { param, id } = mapping;
    if (param === 'masterVolume') { setMasterVolume(normalized); engine.setMasterVolume(normalized); return; }
    if (param === 'busVocal')  { updateBus('vocal', { volume: normalized }); return; }
    if (param === 'busMusic')  { updateBus('music', { volume: normalized }); return; }
    if (param === 'busSfx')    { updateBus('sfx',   { volume: normalized }); return; }
    if (param === 'volume' && id)  { setVolume(id, normalized); return; }
    if (param === 'pan'    && id)  { setPan(id, normalized * 2 - 1); return; }
    if (param === 'eqSub'  && id)  { setEQ(id, 'sub',     (normalized * 24) - 12); return; }
    if (param === 'eqBass' && id)  { setEQ(id, 'bass',    (normalized * 24) - 12); return; }
  };

  const clearMidiMap = () => {
    setMidiMap({});
    localStorage.removeItem('fho_midi_map');
  };

  const updateBus = (busId, params) => {
    const updated = { ...buses[busId], ...params };
    const vol = updated.muted ? 0 : updated.volume;
    engine.setBusVolume(busId, vol);
    setBuses(prev => ({ ...prev, [busId]: updated }));
  };

  // ── Session Management ────────────────────────────────────────────
  const saveSession = () => {
    const name = sessionName.trim() || mixName;
    if (!name) return;
    const session = {
      name,
      savedAt: new Date().toISOString(),
      mixName,
      fadeIn, fadeOut,
      masterVolume,
      buses,
      tracks: Object.fromEntries(
        Object.entries(tracks).map(([id, t]) => [id, {
          volume: t.volume, pan: t.pan, reverb: t.reverb,
          eq: t.eq, comp: t.comp, muted: t.muted,
          fileName: t.fileName, // remembered for reference — file must be reloaded manually
        }])
      ),
    };
    const updated = { ...sessions, [name]: session };
    localStorage.setItem('fho_soundboard_sessions', JSON.stringify(updated));
    setSessions(updated);
    setSessionName('');
    alert(`Session "${name}" saved.`);
  };

  const loadSession = (name) => {
    const s = sessions[name];
    if (!s) return;
    setMixName(s.mixName || name);
    setFadeIn(s.fadeIn ?? 1.0);
    setFadeOut(s.fadeOut ?? 2.0);
    engine.setMasterVolume(s.masterVolume ?? 0.85);
    // Restore buses
    if (s.buses) {
      Object.entries(s.buses).forEach(([busId, b]) => {
        engine.setBusVolume(busId, b.muted ? 0 : b.volume);
      });
      setBuses(s.buses);
    }
    // Restore track settings (not audio — files must be reloaded)
    if (s.tracks) {
      setTracks(prev => {
        const updated = { ...prev };
        Object.entries(s.tracks).forEach(([id, saved]) => {
          updated[id] = {
            ...updated[id],
            volume: saved.volume, pan: saved.pan, reverb: saved.reverb,
            eq: saved.eq, comp: saved.comp, muted: saved.muted,
            fileName: saved.fileName ? `↺ Re-load: ${saved.fileName}` : '',
          };
          if (engine.tracks[id]) {
            engine.setVolume(id, saved.muted ? 0 : saved.volume);
            engine.setPan(id, saved.pan);
            engine.setEQ(id, 'low',  saved.eq.low);
            engine.setEQ(id, 'mid',  saved.eq.mid);
            engine.setEQ(id, 'high', saved.eq.high);
            engine.setCompression(id, saved.comp);
          }
        });
        return updated;
      });
    }
    setShowSessions(false);
  };

  const deleteSession = (name) => {
    if (!window.confirm(`Delete session "${name}"?`)) return;
    const updated = { ...sessions };
    delete updated[name];
    localStorage.setItem('fho_soundboard_sessions', JSON.stringify(updated));
    setSessions(updated);
  };

  // ── Load track from file ──────────────────────────────────────────
  // ── Video loading (Phase 2 Commit 2) ─────────────────────────
  const loadVideoFromFile = async (id, file) => {
    if (!file) return;
    setTracks(prev => ({ ...prev, [id]: { ...prev[id], loading: true, fileName: file.name } }));
    try {
      const url = URL.createObjectURL(file);
      // Extract metadata (duration) and generate thumbnail via temp video element
      const metadata = await new Promise((resolve, reject) => {
        const v = document.createElement('video');
        v.preload = 'metadata';
        v.muted = true;
        v.src = url;
        v.onloadedmetadata = () => {
          // Seek to 1 second in (or 10% of duration, whichever is less) for thumbnail
          v.currentTime = Math.min(1, v.duration * 0.1);
        };
        v.onseeked = () => {
          try {
            const canvas = document.createElement('canvas');
            const maxW = 320;
            const scale = Math.min(1, maxW / v.videoWidth);
            canvas.width = Math.round(v.videoWidth * scale);
            canvas.height = Math.round(v.videoHeight * scale);
            const ctx = canvas.getContext('2d');
            ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
            resolve({ duration: v.duration, thumbnail: canvas.toDataURL('image/jpeg', 0.7) });
          } catch (e) {
            resolve({ duration: v.duration, thumbnail: null });
          }
        };
        v.onerror = () => reject(new Error('Failed to load video metadata'));
      });
      setTracks(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          loaded: true,
          loading: false,
          fileName: file.name,
          url,
          duration: metadata.duration,
          thumbnail: metadata.thumbnail,
        },
      }));
    } catch (err) {
      console.error('Video load error:', err);
      setTracks(prev => ({ ...prev, [id]: { ...prev[id], loading: false, fileName: '' } }));
    }
  };

  const loadTrackFromFile = async (id, file) => {
    if (!file) return;
    setTracks(prev => ({ ...prev, [id]: { ...prev[id], loading: true, fileName: file.name } }));
    try {
      const arrayBuffer = await file.arrayBuffer();
      engine.init();
      const decoded = await engine.ctx.decodeAudioData(arrayBuffer);
      const gainNode = engine.ctx.createGain();
      const eqSub     = engine.ctx.createBiquadFilter();
      const eqBass    = engine.ctx.createBiquadFilter();
      const eqLowMid  = engine.ctx.createBiquadFilter();
      const eqMid     = engine.ctx.createBiquadFilter();
      const eqHighMid = engine.ctx.createBiquadFilter();
      const eqHigh    = engine.ctx.createBiquadFilter();
      const panNode = engine.ctx.createStereoPanner();
      const reverbGain = engine.ctx.createGain();
      const dryGain = engine.ctx.createGain();
      // 6-Band EQ setup
      eqSub.type     = 'lowshelf';  eqSub.frequency.value     = 40;    eqSub.gain.value     = 0;
      eqBass.type    = 'peaking';   eqBass.frequency.value    = 100;   eqBass.gain.value    = 0; eqBass.Q.value    = 1.0;
      eqLowMid.type  = 'peaking';   eqLowMid.frequency.value  = 300;   eqLowMid.gain.value  = 0; eqLowMid.Q.value  = 1.0;
      eqMid.type     = 'peaking';   eqMid.frequency.value     = 1000;  eqMid.gain.value     = 0; eqMid.Q.value     = 1.0;
      eqHighMid.type = 'peaking';   eqHighMid.frequency.value = 5000;  eqHighMid.gain.value = 0; eqHighMid.Q.value = 1.0;
      eqHigh.type    = 'highshelf'; eqHigh.frequency.value    = 12000; eqHigh.gain.value    = 0;
      // Compressor — default passthrough (ratio 1, threshold 0)
      const compressor = engine.ctx.createDynamicsCompressor();
      compressor.threshold.value = 0;
      compressor.knee.value      = 40;
      compressor.ratio.value     = 1;
      compressor.attack.value    = 0.003;
      compressor.release.value   = 0.25;
      // Signal chain: gain → Sub → Bass → LowMid → Mid → HighMid → High → compressor → pan → bus → master
      gainNode.connect(eqSub);
      eqSub.connect(eqBass);
      eqBass.connect(eqLowMid);
      eqLowMid.connect(eqMid);
      eqMid.connect(eqHighMid);
      eqHighMid.connect(eqHigh);
      eqHigh.connect(compressor);
      compressor.connect(panNode);
      const trackBus = engine.getBus(id);
      panNode.connect(dryGain);
      dryGain.connect(trackBus);
      panNode.connect(reverbGain);
      reverbGain.connect(trackBus);
      reverbGain.gain.value = 0;
      dryGain.gain.value = 1;
      engine.tracks[id] = { buffer: decoded, gainNode, eqSub, eqBass, eqLowMid, eqMid, eqHighMid, eqHigh, compressor, panNode, reverbGain, dryGain, source: null, offset: 0, startedAt: 0 };
      engine.setVolume(id, tracks[id].volume);
      engine.setPan(id, tracks[id].pan);
      setTracks(prev => ({ ...prev, [id]: { ...prev[id], loaded: true, loading: false, duration: decoded.duration, currentTime: 0, fileName: file.name } }));
    } catch (e) {
      setTracks(prev => ({ ...prev, [id]: { ...prev[id], loading: false, fileName: '' } }));
      alert('Failed to load audio file. Make sure it is a valid MP3 or WAV file.');
    }
  };

  // ── Playback ──────────────────────────────────────────────────────
  const play = () => {
    const ph = playheadPos;
    playStartRef.current = { ctxTime: engine.ctx ? engine.ctx.currentTime : 0, playhead: ph };
    Object.keys(tracks).forEach(id => {
      if (tracks[id].loaded && !tracks[id].muted) {
        engine.playTimeline(id, ph, tracks[id].startOffset);
      }
    });
    setIsPlaying(true);
  };

  const pause = () => {
    // Capture playhead at pause point
    const ph = playheadPos;
    Object.keys(tracks).forEach(id => {
      if (tracks[id].loaded) engine.pause(id);
    });
    setIsPlaying(false);
  };

  const stop = () => {
    Object.keys(tracks).forEach(id => { engine.stop(id); });
    setIsPlaying(false);
    setPlayheadPos(0);
    setTracks(prev => {
      const u = { ...prev };
      Object.keys(u).forEach(id => { u[id] = { ...u[id], currentTime: 0 }; });
      return u;
    });
  };

  const seekTimeline = (pos) => {
    const newPos = Math.max(0, pos);
    setPlayheadPos(newPos);
    if (isPlaying) {
      Object.keys(tracks).forEach(id => { engine.stop(id); });
      setIsPlaying(false);
    }
  };

  const setTrackOffset = (id, offset) => {
    setTracks(prev => ({ ...prev, [id]: { ...prev[id], startOffset: parseFloat(offset.toFixed(2)) } }));
  };

  const seek = (id, pct) => {
    const t = tracks[id];
    if (!t.loaded) return;
    const newTime = pct * t.duration;
    if (isPlaying) { engine.play(id, newTime); }
    else { engine.pause(id); engine.tracks[id].offset = newTime; }
    setTracks(prev => ({ ...prev, [id]: { ...prev[id], currentTime: newTime } }));
  };

  // ── Track controls ────────────────────────────────────────────────
  const setVolume = (id, val) => {
    engine.setVolume(id, val);
    setTracks(prev => ({ ...prev, [id]: { ...prev[id], volume: val } }));
  };

  const setPan = (id, val) => {
    engine.setPan(id, val);
    setTracks(prev => ({ ...prev, [id]: { ...prev[id], pan: val } }));
  };

  const setReverb = async (id, decay, wet, label) => {
    await engine.setReverb(id, decay, wet);
    setTracks(prev => ({ ...prev, [id]: { ...prev[id], reverb: label } }));
  };

  const setEQ = (id, band, val) => {
    engine.setEQ(id, band, val);
    setTracks(prev => ({ ...prev, [id]: { ...prev[id], eq: { ...prev[id].eq, [band]: val } } }));
  };

  const setCompression = (id, params) => {
    const updated = { ...tracks[id].comp, ...params };
    engine.setCompression(id, updated);
    setTracks(prev => ({ ...prev, [id]: { ...prev[id], comp: updated } }));
  };

  const toggleMute = (id) => {
    const muted = !tracks[id].muted;
    engine.setVolume(id, muted ? 0 : tracks[id].volume);
    setTracks(prev => ({ ...prev, [id]: { ...prev[id], muted } }));
  };

  const toggleSolo = (id) => {
    const solo = !tracks[id].solo;
    setTracks(prev => {
      const u = { ...prev };
      Object.keys(u).forEach(k => {
        const isSolo = k === id ? solo : false;
        u[k] = { ...u[k], solo: isSolo };
        engine.setVolume(k, isSolo ? u[k].volume : solo && k !== id ? 0 : u[k].volume);
      });
      if (!solo) Object.keys(u).forEach(k => engine.setVolume(k, u[k].muted ? 0 : u[k].volume));
      return u;
    });
  };

  // ── Export ────────────────────────────────────────────────────────
  const exportMix = async () => {
    setExportStatus('Rendering mix...');
    try {
      const duration = Math.max(...Object.values(tracks).filter(t => t.loaded).map(t => t.duration));
      if (!duration) { setExportStatus('No audio loaded.'); return; }
      const offlineCtx = new OfflineAudioContext(2, Math.ceil(duration * 44100), 44100);
      const masterGain = offlineCtx.createGain();
      masterGain.gain.value = masterVolume;
      masterGain.connect(offlineCtx.destination);
      // ── Offline bus nodes ─────────────────────────────────────────
      const offlineVocalBus = offlineCtx.createGain();
      const offlineMusicBus = offlineCtx.createGain();
      const offlineSfxBus   = offlineCtx.createGain();
      offlineVocalBus.gain.value = buses.vocal.muted ? 0 : buses.vocal.volume;
      offlineMusicBus.gain.value = buses.music.muted ? 0 : buses.music.volume;
      offlineSfxBus.gain.value   = buses.sfx.muted   ? 0 : buses.sfx.volume;
      offlineVocalBus.connect(masterGain);
      offlineMusicBus.connect(masterGain);
      offlineSfxBus.connect(masterGain);
      const getOfflineBus = (id) => {
        if (['vocalLead','vocalSecondary','vocalBackground'].includes(id)) return offlineVocalBus;
        if (['score','theme'].includes(id)) return offlineMusicBus;
        return offlineSfxBus;
      };

      for (const [id, t] of Object.entries(tracks)) {
        if (t.type === 'video') continue;  // skip video lanes in audio export
        if (!t.loaded || t.muted) continue;
        const buf = engine.tracks[id].buffer;
        const src = offlineCtx.createBufferSource();
        src.buffer = buf;
        const gain = offlineCtx.createGain();
        gain.gain.value = t.volume;
        const pan = offlineCtx.createStereoPanner();
        pan.pan.value = t.pan;
        // ── Fade In automation ────────────────────────────────────────
        if (fadeIn > 0) {
          gain.gain.setValueAtTime(0, 0);
          gain.gain.linearRampToValueAtTime(t.volume, Math.min(fadeIn, buf.duration));
        }
        // ── Fade Out automation ───────────────────────────────────────
        if (fadeOut > 0 && buf.duration > fadeOut) {
          gain.gain.setValueAtTime(t.volume, buf.duration - fadeOut);
          gain.gain.linearRampToValueAtTime(0, buf.duration);
        }
        src.connect(gain);
        gain.connect(pan);
        pan.connect(getOfflineBus(id));
        src.start(0);
      }

      const rendered = await offlineCtx.startRendering();
      const wav = audioBufferToWav(rendered);
      const blob = new Blob([wav], { type: 'audio/wav' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${mixName}.wav`;
      a.click();
      setExportStatus('✓ Mix exported as WAV');
      setTimeout(() => setExportStatus(''), 3000);
    } catch (e) {
      setExportStatus('Export failed. ' + e.message);
    }
  };

  function audioBufferToWav(buffer) {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const length = buffer.length;
    const data = new Float32Array(length * numChannels);
    for (let ch = 0; ch < numChannels; ch++) {
      const chData = buffer.getChannelData(ch);
      for (let i = 0; i < length; i++) data[i * numChannels + ch] = chData[i];
    }
    const pcm = new Int16Array(data.length);
    for (let i = 0; i < data.length; i++) pcm[i] = Math.max(-32768, Math.min(32767, data[i] * 32768));
    const wavBuffer = new ArrayBuffer(44 + pcm.byteLength);
    const view = new DataView(wavBuffer);
    const write = (off, str) => { for (let i = 0; i < str.length; i++) view.setUint8(off + i, str.charCodeAt(i)); };
    write(0, 'RIFF'); view.setUint32(4, 36 + pcm.byteLength, true);
    write(8, 'WAVE'); write(12, 'fmt '); view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * numChannels * 2, true);
    view.setUint16(32, numChannels * 2, true); view.setUint16(34, 16, true);
    write(36, 'data'); view.setUint32(40, pcm.byteLength, true);
    new Int16Array(wavBuffer, 44).set(pcm);
    return wavBuffer;
  }

  const anyLoaded = Object.values(tracks).some(t => t.loaded);

  return (
    <div style={{ minHeight: '100vh', background: NAVY, color: '#fff', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

      {/* Header */}
      <div style={{ padding: '16px 28px', borderBottom: '1px solid rgba(212,175,55,0.15)', background: 'rgba(10,15,30,0.98)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <span style={{ fontSize: '22px' }}>🎚️</span>
          <div>
            <div style={{ fontSize: '1.1rem', fontWeight: '700', color: GOLD, letterSpacing: '0.04em' }}>MindStreams Studio</div>
            <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Mixer</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => navigate('/mindstreams-studio')} style={{ ...btn, fontSize: '0.8rem' }}>← Studio</button>
          <button onClick={() => window.open('https://freehappyone.com/docs/MindStreams_Soundboard_User_Guide.docx', '_blank')} style={{ ...btn, fontSize: '0.8rem', color: GOLD, border: `1px solid ${GOLD}44` }}>
            📖 User Guide
          </button>
          <button onClick={() => setShowTimeline(v => !v)} style={{ ...btn, fontSize: '0.8rem', background: showTimeline ? `${GOLD}22` : 'rgba(255,255,255,0.06)', border: showTimeline ? `1px solid ${GOLD}` : '1px solid rgba(255,255,255,0.1)', color: showTimeline ? GOLD : 'rgba(255,255,255,0.7)' }}>
            {showTimeline ? '⏱ Timeline ▲' : '⏱ Timeline ▼'}
          </button>
          <button onClick={() => setShowMidi(v => !v)} style={{ ...btn, fontSize: '0.8rem', background: showMidi ? 'rgba(0,255,157,0.12)' : 'rgba(255,255,255,0.06)', border: showMidi ? '1px solid #00ff9d' : '1px solid rgba(255,255,255,0.1)', color: showMidi ? '#00ff9d' : 'rgba(255,255,255,0.7)' }}>
            {midiEnabled && midiDevice ? '🎹 MIDI ●' : '🎹 MIDI'}
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ borderBottom: '1px solid rgba(212,175,55,0.15)', background: 'rgba(10,15,30,0.95)', padding: '0 28px', display: 'flex', gap: '4px' }}>
        {[['soundboard', '🎚️ Soundboard'], ['mixer', '🎬 Video Board']].map(([tab, label]) => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '12px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: '13px', fontWeight: '700',
            color: activeTab === tab ? GOLD : 'rgba(255,255,255,0.4)',
            borderBottom: activeTab === tab ? `2px solid ${GOLD}` : '2px solid transparent',
            fontFamily: "'Segoe UI', system-ui, sans-serif",
            transition: 'all 0.15s',
          }}>{label}</button>
        ))}
      </div>

      {/* Video Board (Mixer Panel) */}
      {activeTab === 'mixer' && <MixerPanel />}

      {activeTab === 'soundboard' && <div style={{ padding: '28px', maxWidth: '1100px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>

        {/* Video Preview (Phase 2 Commit 3) */}
        <div style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '16px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Video Preview</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              {VIDEO_LANE_ORDER.map(({ id, label, color }) => {
                const isActive = activePreview === id;
                const isLoaded = tracks[id].loaded;
                return (
                  <button
                    key={id}
                    onClick={() => setActivePreview(id)}
                    disabled={!isLoaded}
                    style={{
                      ...btn,
                      fontSize: '0.75rem',
                      padding: '6px 14px',
                      background: isActive ? `${color}22` : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${isActive ? color : 'rgba(255,255,255,0.1)'}`,
                      color: isActive ? color : 'rgba(255,255,255,0.5)',
                      opacity: isLoaded ? 1 : 0.3,
                    }}
                  >
                    {label}{isLoaded ? '' : ' (empty)'}
                  </button>
                );
              })}
              <button
                onClick={() => setTracks(prev => ({ ...prev, [activePreview]: { ...prev[activePreview], videoMuted: !prev[activePreview].videoMuted } }))}
                disabled={!tracks[activePreview].loaded}
                style={{
                  ...btn,
                  fontSize: '0.8rem',
                  padding: '6px 10px',
                  background: tracks[activePreview].videoMuted ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${tracks[activePreview].videoMuted ? '#ef4444' : 'rgba(255,255,255,0.1)'}`,
                  color: tracks[activePreview].videoMuted ? '#ef4444' : 'rgba(255,255,255,0.6)',
                  opacity: tracks[activePreview].loaded ? 1 : 0.3,
                }}
                title={tracks[activePreview].videoMuted ? 'Embedded audio muted' : 'Embedded audio on'}
              >
                {tracks[activePreview].videoMuted ? '🔇' : '🔊'}
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '150px' }}>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={Math.round(((tracks[activePreview].videoVolume == null ? 1.0 : tracks[activePreview].videoVolume)) * 100)}
                  onChange={e => {
                    const v = parseInt(e.target.value, 10) / 100;
                    setTracks(prev => ({ ...prev, [activePreview]: { ...prev[activePreview], videoVolume: v } }));
                  }}
                  disabled={!tracks[activePreview].loaded}
                  style={{ flex: 1, accentColor: '#ef4444', cursor: tracks[activePreview].loaded ? 'pointer' : 'not-allowed', opacity: tracks[activePreview].loaded ? 1 : 0.3 }}
                  title="Video embedded audio volume"
                />
                <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', minWidth: '32px', textAlign: 'right' }}>
                  {Math.round(((tracks[activePreview].videoVolume == null ? 1.0 : tracks[activePreview].videoVolume)) * 100)}%
                </span>
              </div>
              <button
                onClick={isPlaying ? pause : play}
                disabled={!anyLoaded}
                style={{
                  ...btn,
                  fontSize: '1.0rem',
                  padding: '6px 16px',
                  background: isPlaying ? 'rgba(212,175,55,0.15)' : `${GOLD}22`,
                  border: `1px solid ${GOLD}`,
                  color: GOLD,
                  opacity: !anyLoaded ? 0.3 : 1,
                  fontWeight: '700',
                }}
                title={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? '⏸' : '▶'}
              </button>
            </div>
          </div>

          <div style={{ background: '#000', borderRadius: '8px', overflow: 'hidden', aspectRatio: '16 / 9', maxWidth: '100%', position: 'relative' }}>
            {VIDEO_LANE_ORDER.map(({ id }) => {
              const track = tracks[id];
              const isActive = activePreview === id;
              return (
                <video
                  key={id}
                  ref={el => { videoElsRef.current[id] = el; }}
                  src={track.url || undefined}
                  style={{
                    width: '100%',
                    height: '100%',
                    display: isActive ? 'block' : 'none',
                    objectFit: 'contain',
                    background: '#000',
                  }}
                  playsInline
                  preload="auto"
                />
              );
            })}
            {!tracks[activePreview].loaded && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.25)', fontSize: '0.9rem', letterSpacing: '0.04em' }}>
                {tracks.videoPrimary.loaded || tracks.videoBroll.loaded
                  ? `Select a loaded lane above`
                  : `Upload a video clip in "Load Video Tracks" below`}
              </div>
            )}
          </div>
        </div>

        {/* Transport + Master */}
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'stretch' }}>

          {/* Transport */}
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '16px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', flex: '0 0 auto' }}>
            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Transport</div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button onClick={stop} style={{ ...btn, fontSize: '1.2rem', padding: '10px 16px' }} title="Stop">⏹</button>
              <button
                onClick={isPlaying ? pause : play}
                disabled={!anyLoaded}
                style={{ ...btn, fontSize: '1.4rem', padding: '10px 20px', background: isPlaying ? 'rgba(212,175,55,0.15)' : `${GOLD}22`, border: `1px solid ${GOLD}`, color: GOLD, opacity: !anyLoaded ? 0.3 : 1 }}
              >
                {isPlaying ? '⏸' : '▶'}
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: isPlaying ? '#00ff9d' : 'rgba(255,255,255,0.2)', boxShadow: isPlaying ? '0 0 8px #00ff9d' : 'none', transition: 'all 0.3s' }} />
              <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>{isPlaying ? 'Playing' : 'Stopped'}</span>
            </div>
          </div>

          {/* Master */}
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '16px', padding: '20px', display: 'flex', gap: '20px', alignItems: 'flex-start', flex: '0 0 auto' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Master Volume</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px' }}>
                <Fader value={masterVolume} onChange={setMasterVolume} color={GOLD} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <VUMeter level={masterVolume * (isPlaying ? 0.7 + Math.random() * 0.3 : 0)} />
                  <VUMeter level={masterVolume * (isPlaying ? 0.6 + Math.random() * 0.4 : 0)} />
                </div>
              </div>
            </div>
          </div>

          {/* Spectrum */}
          <div style={{ flex: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '16px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '280px' }}>
            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Spectrum Analyzer</div>
            <SpectrumAnalyzer isPlaying={isPlaying} />
          </div>
        </div>

        {/* Timeline */}
        {showTimeline && (
          <TimelineView
            tracks={tracks}
            playheadPos={playheadPos}
            zoom={timelineZoom}
            onSeek={seekTimeline}
            onOffsetChange={setTrackOffset}
            onZoomChange={setTimelineZoom}
          />
        )}

        {/* MIDI Panel */}
        {showMidi && (
          <MidiPanel
            midiEnabled={midiEnabled}
            midiDevices={midiDevices}
            midiDevice={midiDevice}
            midiLearn={midiLearn}
            midiMap={midiMap}
            onInit={initMidi}
            onConnect={connectMidiDevice}
            onLearn={setMidiLearn}
            onClearMap={clearMidiMap}
          />
        )}

        {/* Mix Buses */}
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', padding: '20px' }}>
          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '14px' }}>Mix Buses</div>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
            <BusStrip busId="vocal" label="Vocal Bus"  color={PURPLE}   bus={buses.vocal} onChange={(p) => updateBus('vocal', p)} />
            <BusStrip busId="music" label="Music Bus"  color={GOLD}     bus={buses.music} onChange={(p) => updateBus('music', p)} />
            <BusStrip busId="sfx"   label="SFX Bus"    color={GREEN}    bus={buses.sfx}   onChange={(p) => updateBus('sfx',   p)} />
          </div>
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.2)', marginTop: '12px' }}>
            Vocal Bus — Lead, Secondary, Background &nbsp;·&nbsp; Music Bus — Score, Theme &nbsp;·&nbsp; SFX Bus — Foley, Ambient, Cinematic
          </div>
        </div>

        {/* Session Management */}
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Sessions</div>
            <button onClick={() => setShowSessions(v => !v)} style={{ ...btn, fontSize: '0.75rem', padding: '4px 12px', color: GOLD, border: `1px solid ${GOLD}44` }}>
              {showSessions ? '▲ Hide' : `▼ Saved (${Object.keys(sessions).length})`}
            </button>
          </div>

          {/* Save row */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={sessionName}
              onChange={e => setSessionName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveSession(); }}
              placeholder={`Session name (e.g. "Episode 7 Mix")`}
              style={{ flex: 1, minWidth: '220px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', padding: '9px 14px', fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit' }}
            />
            <button onClick={saveSession} style={{ ...btn, background: `${GOLD}22`, border: `1px solid ${GOLD}`, color: GOLD, fontWeight: '700', padding: '9px 20px', whiteSpace: 'nowrap' }}>
              💾 Save Session
            </button>
          </div>

          {/* Session list */}
          {showSessions && (
            <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {Object.keys(sessions).length === 0 ? (
                <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.2)', textAlign: 'center', padding: '12px' }}>No saved sessions yet.</div>
              ) : Object.entries(sessions).sort((a, b) => new Date(b[1].savedAt) - new Date(a[1].savedAt)).map(([name, s]) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.85rem', color: GOLD, fontWeight: '600' }}>{name}</div>
                    <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>
                      Saved {new Date(s.savedAt).toLocaleDateString()} at {new Date(s.savedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {s.tracks && ` · ${Object.values(s.tracks).filter(t => t.fileName && !t.fileName.startsWith('↺')).length} files`}
                    </div>
                  </div>
                  <button onClick={() => loadSession(name)} style={{ ...btn, background: `${GOLD}18`, border: `1px solid ${GOLD}55`, color: GOLD, fontSize: '0.75rem', padding: '5px 14px' }}>Load</button>
                  <button onClick={() => deleteSession(name)} style={{ ...btn, background: 'rgba(255,60,60,0.08)', border: '1px solid rgba(255,60,60,0.25)', color: '#ff6060', fontSize: '0.75rem', padding: '5px 10px' }}>✕</button>
                </div>
              ))}
            </div>
          )}

          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.2)', marginTop: '10px' }}>
            Saves all track settings, EQ, compression, reverb, bus levels, and fade values. Audio files must be reloaded — sessions remember filenames as reference.
          </div>
        </div>

        {/* File Loaders */}
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', padding: '20px' }}>
          {/* Load Video Tracks (Phase 2 Commit 2) */}
          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '14px' }}>Load Video Tracks</div>
          <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.25)', marginBottom: '14px' }}>
            Upload MP4 clips from Topview AI or other sources — video plays alongside your audio mix.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
            {VIDEO_LANE_ORDER.map(({ id, label, color }) => (
              <div key={id} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', color, minWidth: '160px', fontWeight: '600' }}>🎬 {label}</span>
                <input
                  key={fileKeys[id]}
                  ref={el => { fileRefsObj.current[id] = el; }}
                  type="file"
                  accept="video/mp4,video/*"
                  style={{ display: 'none' }}
                  onChange={e => {
                    if (e.target.files[0]) {
                      loadVideoFromFile(id, e.target.files[0]);
                      setFileKeys(prev => ({ ...prev, [id]: prev[id] + 1 }));
                    }
                  }}
                />
                <button
                  onClick={() => { if (fileRefsObj.current[id]) fileRefsObj.current[id].click(); }}
                  disabled={tracks[id].loading}
                  style={{ ...btn, background: `${color}18`, border: `1px solid ${color}55`, color, padding: '9px 18px', whiteSpace: 'nowrap' }}
                >
                  {tracks[id].loading ? '⟳ Loading...' : tracks[id].loaded ? '↺ Replace Clip' : '📁 Choose Clip'}
                </button>
                {tracks[id].thumbnail && (
                  <img
                    src={tracks[id].thumbnail}
                    alt={`${label} thumbnail`}
                    style={{ width: '80px', height: '45px', objectFit: 'cover', borderRadius: '4px', border: `1px solid ${color}55` }}
                  />
                )}
                {tracks[id].fileName && (
                  <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '240px' }}>
                    ✓ {tracks[id].fileName}{tracks[id].duration ? ` · ${tracks[id].duration.toFixed(1)}s` : ''}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Load Audio Tracks */}
          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '14px' }}>Load Audio Tracks</div>
          <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.25)', marginBottom: '14px' }}>
            Select downloaded MP3 or WAV files from your machine — generated vocals and instrumentals from MindStreams Studio.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              { id: 'vocalLead',       label: '🎤 Vocal — Lead',        color: PURPLE },
              { id: 'vocalSecondary',  label: '🎤 Vocal — Secondary',    color: '#9b30ff' },
              { id: 'vocalBackground', label: '🎤 Vocal — Background',   color: '#c084fc' },
              { id: 'score',           label: '🎵 Instrumental — Score',  color: GOLD   },
              { id: 'theme',           label: '🎵 Instrumental — Theme',  color: '#f59e0b' },
              { id: 'sfxFoley',        label: '✨ SFX — Foley',           color: GREEN  },
              { id: 'sfxAmbient',      label: '✨ SFX — Ambient',         color: '#34d399' },
              { id: 'sfxCinematic',    label: '✨ SFX — Cinematic',       color: '#6ee7b7' },
            ].map(({ id, label, color }) => (
              <div key={id} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', color, minWidth: '160px', fontWeight: '600' }}>{label}</span>
                <input
                  key={fileKeys[id]}
                  ref={el => { fileRefsObj.current[id] = el; }}
                  type="file"
                  accept="audio/mp3,audio/mpeg,audio/wav,audio/*"
                  style={{ display: 'none' }}
                  onChange={e => {
                    if (e.target.files[0]) {
                      loadTrackFromFile(id, e.target.files[0]);
                      setFileKeys(prev => ({ ...prev, [id]: prev[id] + 1 }));
                    }
                  }}
                />
                <button
                  onClick={() => { if (fileRefsObj.current[id]) fileRefsObj.current[id].click(); }}
                  disabled={tracks[id].loading}
                  style={{ ...btn, background: `${color}18`, border: `1px solid ${color}55`, color, padding: '9px 18px', whiteSpace: 'nowrap' }}
                >
                  {tracks[id].loading ? '⟳ Loading...' : tracks[id].loaded ? '↺ Replace File' : '📁 Choose File'}
                </button>
                {tracks[id].fileName ? (
                  <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '300px' }}>
                    ✓ {tracks[id].fileName}
                  </span>
                ) : (
                  <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.2)' }}>No file selected</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Track Strips */}
        {[
          { id: 'vocalLead',       label: 'Vocal — Lead',        color: PURPLE   },
          { id: 'vocalSecondary',  label: 'Vocal — Secondary',   color: '#9b30ff' },
          { id: 'vocalBackground', label: 'Vocal — Background',  color: '#c084fc' },
          { id: 'score',           label: 'Instrumental — Score', color: GOLD     },
          { id: 'theme',           label: 'Instrumental — Theme', color: '#f59e0b' },
          { id: 'sfxFoley',        label: 'SFX — Foley',          color: GREEN    },
          { id: 'sfxAmbient',      label: 'SFX — Ambient',        color: '#34d399' },
          { id: 'sfxCinematic',    label: 'SFX — Cinematic',      color: '#6ee7b7' },
        ].map(({ id, label, color }) => (
          <TrackStrip
            key={id}
            id={id}
            label={label}
            color={color}
            track={tracks[id]}
            isPlaying={isPlaying}
            onVolumeChange={(v) => setVolume(id, v)}
            onPanChange={(v) => setPan(id, v)}
            onReverbChange={(decay, wet) => {
              const preset = REVERB_PRESETS.find(p => p.decay === decay && p.wet === wet);
              setReverb(id, decay, wet, preset?.label || 'Custom');
            }}
            onEQChange={(band, val) => setEQ(id, band, val)}
            onCompChange={(params) => setCompression(id, params)}
            onSeek={(pct) => seek(id, pct)}
            onMute={() => toggleMute(id)}
            onSolo={() => toggleSolo(id)}
          />
        ))}

        {/* Export */}
        <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${GOLD}33`, borderRadius: '16px', padding: '20px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase', flexBasis: '100%' }}>Export Mix</div>

          {/* Filename */}
          <input
            type="text"
            value={mixName}
            onChange={e => setMixName(e.target.value)}
            placeholder="Mix filename..."
            style={{ flex: 1, minWidth: '200px', background: 'rgba(255,255,255,0.05)', border: `1px solid rgba(255,255,255,0.1)`, borderRadius: '8px', color: '#fff', padding: '10px 14px', fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit' }}
          />

          {/* Fade In */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '120px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Fade In</span>
              <span style={{ fontSize: '0.75rem', color: GOLD }}>{fadeIn.toFixed(1)}s</span>
            </div>
            <input
              type="range" min={0} max={10} step={0.1} value={fadeIn}
              onChange={e => setFadeIn(parseFloat(e.target.value))}
              style={{ width: '100%', accentColor: GOLD, cursor: 'pointer' }}
            />
          </div>

          {/* Fade Out */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '120px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Fade Out</span>
              <span style={{ fontSize: '0.75rem', color: GOLD }}>{fadeOut.toFixed(1)}s</span>
            </div>
            <input
              type="range" min={0} max={10} step={0.1} value={fadeOut}
              onChange={e => setFadeOut(parseFloat(e.target.value))}
              style={{ width: '100%', accentColor: GOLD, cursor: 'pointer' }}
            />
          </div>

          {/* Export Button */}
          <button
            onClick={exportMix}
            disabled={!anyLoaded}
            style={{ ...btn, background: anyLoaded ? GOLD : 'rgba(255,255,255,0.06)', color: anyLoaded ? NAVY : 'rgba(255,255,255,0.3)', fontWeight: '700', padding: '10px 24px', opacity: !anyLoaded ? 0.5 : 1 }}
          >
            ↓ Export WAV
          </button>

          {exportStatus && <span style={{ fontSize: '0.85rem', color: exportStatus.startsWith('✓') ? GREEN : '#f87171' }}>{exportStatus}</span>}
          <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.25)', flexBasis: '100%' }}>
            Exports all non-muted tracks mixed to a single stereo WAV — with fade in, fade out, reverb, EQ, and compression applied. Set fade to 0s to disable.
          </div>
        </div>

      </div>}
    </div>
  );
}
